import type {Entity} from 'src/types/analytics-genesis';

// ---------------------------------------------------------------------------
// Widget types
// ---------------------------------------------------------------------------
// analytics_widget_definition.widget_type: kpi | table | chart only.
// 'section' is a client-side layout primitive — never from catalog/preview/query.

export const WIDGET_TYPE = {
  KPI: 'kpi',
  CHART: 'chart',
  TABLE: 'table',
  SECTION: 'section',
} as const;

export const API_WIDGET_TYPES = [
  WIDGET_TYPE.KPI,
  WIDGET_TYPE.CHART,
  WIDGET_TYPE.TABLE,
] as const;
export type ApiWidgetType = typeof API_WIDGET_TYPES[number];

export const WIDGET_TYPES = [...API_WIDGET_TYPES, WIDGET_TYPE.SECTION] as const;
export type WidgetType = typeof WIDGET_TYPES[number];

/** True for the KPI widget — it uses the compact KPI layout/row/icon logic. */
export function isKpiWidgetType(
  widgetType: string | null | undefined,
): boolean {
  return widgetType === WIDGET_TYPE.KPI;
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
// Frontend canvas model. Stored as-is in backend layout_json (opaque blob).
// Backend returns whatever was stored — no parsing or transformation.

export const ROW_TYPE = {
  KPI: 'kpi',
  VIZ: 'viz',
  SECTION: 'section',
} as const;
export type RowType = typeof ROW_TYPE[keyof typeof ROW_TYPE];

export type Row = {
  id: string; // stable UUID, generated once on creation
  type: RowType;
  tiles: string[]; // ordered placement_ids (empty for section rows)
  label?: string; // section rows only
};

export type Layout = Row[];

// Row capacity: kpi=4, viz=2, section=0 (enforced by store)

// ---------------------------------------------------------------------------
// Dashboard status
// ---------------------------------------------------------------------------

export const DASHBOARD_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
} as const;
export type DashboardStatus =
  typeof DASHBOARD_STATUS[keyof typeof DASHBOARD_STATUS];

// ---------------------------------------------------------------------------
// User reference — all created_by / updated_by fields
// ---------------------------------------------------------------------------

export type UserRef = {
  id: string;
  name: string;
  is_sense?: boolean; // frontend extension — not in backend type
};

// ---------------------------------------------------------------------------
// Canvas widget model (frontend store)
// ---------------------------------------------------------------------------

export type WidgetConfig = {
  widget_id: string; // placement_id
  widget_type: WidgetType;
  widget_key: string;
  label: string;
  widget_icon?: string | null;
  widget_filters_json: Record<string, unknown>;
  widget_config_json: Record<string, unknown>;
  /**
   * Filter keys this widget understands (from the catalog `widget_def`). Used to
   * project host-injected filters per tile: a tile only receives the injected
   * filters it supports (date keys always pass). `undefined` = unknown (e.g. a
   * just-added builder widget) → no projection, all injected filters pass.
   */
  supported_filters?: string[];
  /**
   * True when this placement's catalog definition was deleted (`widget_def` is
   * null). The tile renders an "unavailable" state instead of querying.
   */
  is_orphaned?: boolean;
};

// ---------------------------------------------------------------------------
// Date range
// ---------------------------------------------------------------------------

export type DateRange = {
  startDate: string;
  endDate: string;
  timezone: string;
};

// ---------------------------------------------------------------------------
// Filter payloads (host-injected → forwarded to /query)
// ---------------------------------------------------------------------------

/**
 * Map of filter key → value, injected by a host and forwarded to a tile's
 * `/query`. Values are `unknown` (opaque) on purpose: flat values (string /
 * string[]) cover the common case, but special-cased filters carry a CONVERTED
 * structured wire shape (e.g. `workflow_node_mappings` → `[{workflow_id,
 * node_id}]`, entity → `{and: [...]}`) that the embed passes through without
 * inspecting. The HOST that builds the payload owns the value shape and the
 * backend contract; per-tile `supported_filters` projection only decides WHICH
 * keys reach a given widget, never how a value is structured.
 *
 * NOTE: distinct from `ByodFilterValue` (the RICH, in-memory selection value the
 * builder/panels edit) — this is the already-converted WIRE map sent to `/query`.
 */
export type ByodFilterMap = Record<string, unknown>;

/**
 * React Query fetch lifecycle for a tile. `'idle'` whenever the query is
 * disabled (not yet scrolled into view, failure-locked, or a drag-overlay
 * clone); `'fetching'` while a request is in flight; `'paused'` rarely
 * (offline). Shared by `useTileData`'s result and the presentational
 * `DashboardTile` so the two can't drift.
 */
export type TileFetchStatus = 'idle' | 'fetching' | 'paused';

// ---------------------------------------------------------------------------
// Filter definitions
// ---------------------------------------------------------------------------

export type FilterType =
  | 'date_range_start'
  | 'date_range_end'
  | 'select'
  | 'text'
  | 'number';

export type FilterDefinition = {
  key: string;
  label: string;
  filter_type: FilterType;
  selection_mode: 'single' | 'multi';
  value_type: 'string' | 'date' | 'number';
  default_operator: string | null;
  static_options: {key: string; value: string}[] | null;
  options_query: string | null;
};

export type FilterOption = {key: string; value: string};

// ---------------------------------------------------------------------------
// Structured (special-cased) filter values
// ---------------------------------------------------------------------------
// Most BYOD filters are plain string[] selections rendered by ByodFilterField.
// A few filter keys (see SPECIAL_FILTER_KEYS in ../components/analytics-genesis/
// byod/constants.ts) render a dedicated structured picker instead and therefore
// carry a richer value through the in-memory selection map.
//
// The persisted value (widget_filters_json, already Record<string, unknown>)
// keeps labels so editing a saved widget rehydrates with names. The wire shape
// the backend wants is intentionally different and is produced by converters in
// ../components/analytics-genesis/byod/utils/structured-filters.ts on the read
// paths (/query, /preview) ONLY — never on save.

/** One workflow row: a single workflow and the nodes selected within it. */
export type WorkflowNodeRow = {
  /**
   * Local React row id. Present only in component state; the picker emits and
   * persists rows WITHOUT it so two semantically-identical filters produce a
   * stable widget_filters_json (no query-key churn / false dirty diffs).
   */
  id?: string;
  workflow_ids: Array<{key: string; label: string}>;
  node_ids: Array<{key: string; label: string}>;
};

/** Rich value persisted for the `workflow_node_mappings` filter. */
export type WorkflowNodeMappingValue = {
  workflows: WorkflowNodeRow[];
  show_all: boolean;
};

/**
 * Rich value persisted for the entity filter — the analytics `Entity` shape
 * (entity type + attribute schema + value). Persisted id-less (each row's local
 * React id is held in component state only, see WorkflowNodeRow.id). Kept
 * structurally identical to what the reused EntitiesFilter consumes and what
 * `transformEntities` (src/utils/analytics-genesis.ts) converts to the wire
 * `{and: [...]}` predicate, so the round-trip needs no lossy remap.
 */
export type EntityFilterValue = Array<Entity>;

/**
 * Value a single BYOD filter can hold in the in-memory selection map.
 * Plain `string[]` for generic select filters; a structured value for the
 * special-cased keys. Storage/transport layers are already `unknown`-typed and
 * pass any of these through untouched; the `string[]`-assuming hops must use the
 * SPECIAL_FILTER_KEYS guard to avoid corrupting the structured variants.
 */
export type ByodFilterValue =
  | string[]
  | WorkflowNodeMappingValue
  | EntityFilterValue;

/**
 * The rich, label-carrying in-memory BYOD selection map (pre-wire), keyed by
 * filter key. This is what the pickers edit. Distinct from `ByodFilterMap`: the
 * latter is the LOSSY wire projection of this — produced by
 * `convertStructuredFiltersForWire(getActiveFilters(...))` for /query — and
 * cannot be reversed back into a selection.
 */
export type ByodFilterSelection = Record<string, ByodFilterValue>;

// ---------------------------------------------------------------------------
// Widget catalog
// ---------------------------------------------------------------------------

export type CatalogWidget = {
  widget_key: string;
  widget_type: ApiWidgetType;
  name: string;
  description?: string;
  product_area: string | null;
  // populated lazily via GET /widgets/:widget_key — not in list response
  supported_filters?: string[];
  supported_config?: string[];
};

export type CatalogGroup = {
  label: string;
  widgets: CatalogWidget[];
};

// ---------------------------------------------------------------------------
// Reports library (paginated widget list)
// ---------------------------------------------------------------------------

export type ReportListItem = {
  id: string;
  widget_key: string;
  name: string;
  description: string;
  widget_type: ApiWidgetType;
  product_area: string | null;
  widget_icon: string | null;
  is_global: boolean;
  agency_ids: string[];
  agency_names: string[];
  created_by: UserRef | null;
  updated_by: UserRef | null;
  usage_count: number;
  time_created: string | null;
};

export type ReportListResponse = {
  widgets: ReportListItem[];
  pagination: {total_count: number; page_size: number; offset: number};
};

// ---------------------------------------------------------------------------
// Dashboard list
// ---------------------------------------------------------------------------

export type DashboardListItem = {
  id: string;
  name: string;
  description: string | null;
  /**
   * Filter keys the dashboard exposes as dashboard-level (global) filters. The
   * user picks VALUES for these at view time and the UI merges the selections
   * into every widget's `widget_filters` for `/query` — this is NOT saved values.
   */
  global_filters: string[];
  created_by: UserRef | null;
  updated_by: UserRef | null;
  time_created: string | null;
  // Not in DashboardSummary spec but returned by some backends; used as
  // a display fallback: time_updated ?? time_created
  time_updated?: string | null;
};

export type DashboardListResponse = {
  dashboards: DashboardListItem[];
  pagination: {total_count: number; page_size: number; offset: number};
};

// ---------------------------------------------------------------------------
// Landing dashboard (GET /dashboards?is_landing=true)
// ---------------------------------------------------------------------------

export type LandingDashboard = {
  id: string;
  name: string;
  description: string | null;
  agency_id: string;
  is_shared: boolean;
  is_sense: boolean;
};

export type LandingDashboardResponse = {
  dashboard: LandingDashboard | null;
};

// ---------------------------------------------------------------------------
// Dashboard detail (GET /dashboards/:id)
// ---------------------------------------------------------------------------

// Placement as returned by the API — widget_type lives inside widget_def only.
// Per-placement overrides (label, widget_icon, widget_type) are stored inside
// the `widget_config` JSON blob and extracted at hydration time.
export type PlacementConfig = {
  id: string; // placement_id
  dashboard_id: string;
  widget_key: string;
  widget_filters: Record<string, unknown>;
  widget_config: Record<string, unknown>;
};

/** Whether a searchable column is a grouping dimension or a numeric measure. */
export type SearchableColumnType = 'dimension' | 'measure';

/** One filterable column from `table_config.searchable_columns`. */
export type SearchableColumn = {
  key: string;
  label: string;
  type: SearchableColumnType;
};

export type TableConfig = {
  default_sort?: {column: string; direction: 'asc' | 'desc'};
  /**
   * Columns the backend can search on. Present + non-empty ⇒ the table shows
   * the inline "Filter" button and offers exactly these columns as filter
   * fields. Absent/empty ⇒ no filtering offered.
   */
  searchable_columns?: SearchableColumn[];
};

// Chart styles supported by UniversalChart (mirrors the design system).
export type ChartStyle =
  | 'column'
  | 'line'
  | 'stackedBar'
  | 'stackedBarLine'
  | 'spider'
  | 'donut'
  | 'funnel'
  | 'sankey';

/**
 * KPI icon accent. Mirrors the design-system `AlertSemanticType` accepted by
 * KPIBox — chosen per widget and persisted under `KPI_SEMANTIC_CONFIG_KEY` in
 * `widget_config_json`. Only affects the KPI icon tint.
 */
export type KpiSemantic =
  | 'neutral'
  | 'success'
  | 'information'
  | 'warning'
  | 'danger';

export const KPI_SEMANTICS: readonly KpiSemantic[] = [
  'neutral',
  'success',
  'information',
  'warning',
  'danger',
] as const;

/** Narrow an unknown persisted value to a valid KpiSemantic. */
export function isKpiSemantic(value: unknown): value is KpiSemantic {
  return (
    typeof value === 'string' &&
    (KPI_SEMANTICS as readonly string[]).includes(value)
  );
}

export type ChartAxis = {
  key: string;
  label?: string;
  type?: 'date' | 'category' | 'number';
  field_type?: 'dimension' | 'measure';
};

/**
 * Backend unit hint for a chart series. Drives axis ticks and tooltip
 * suffixes (see `isPercentField`). Open-ended via `& {}` so an unrecognized
 * backend value still type-checks while preserving autocomplete on known
 * literals.
 */
export type ChartSeriesType = 'number' | 'percent' | 'rate' | (string & {});

export type ChartSeries = {
  key: string;
  label: string;
  type?: ChartSeriesType;
  field_type?: 'dimension' | 'measure';
};

// Chart config from the backend. See plans/byod-chart-config-contract.md.
// x_axis may arrive as a single object or an array depending on the endpoint.
//
// For combo (`stackedBarLine`), backend sends `y1_axis` (bars) and
// `y2_axis` (line) instead of `y_axis`. Both pools are independent — a
// field key may appear in both deliberately.
export type ChartConfig = {
  default_chart_style?: ChartStyle;
  /**
   * Stacked-bar orientation (`stackedBar` style only). `horizontal` = categories
   * on the y-axis (the design-system default); `vertical` = columns with
   * categories on the x-axis. Ignored by every other chart style.
   */
  orientation?: 'horizontal' | 'vertical';

  x_axis?: ChartAxis | ChartAxis[];
  y_axis?: ChartSeries[];
  /** Combo (`stackedBarLine`) only — bar series pool. */
  y1_axis?: ChartSeries[];
  /** Combo (`stackedBarLine`) only — line series pool. */
  y2_axis?: ChartSeries[];

  /** Donut (`donut` style only) — subtext under the center total, e.g. "Callers". */
  centerSubtext?: string;
  /** Donut only — render the summed total in the donut's center. */
  showTotal?: boolean;
  /**
   * Donut only — show each slice's value beside its legend entry. Defaults to
   * true in `ByodChart` when unset (the existing behavior).
   */
  showLegendValues?: boolean;

  label_type?: string;
};

export type WidgetDefSummary = {
  id: string;
  name: string;
  widget_type: ApiWidgetType;
  product_area: string | null;
  widget_icon: string | null;
  supported_filters: string[];
  supported_config: string[];
  table_config?: TableConfig | null;
  chart_config?: ChartConfig | null;
};

// Placement with embedded widget definition (GET /dashboards/:id and GET /dashboards/draft)
export type PlacementWithDef = PlacementConfig & {
  widget_def: WidgetDefSummary | null;
  // selected_columns only present in GET /dashboards/:id, not in draft
  selected_columns?: string[] | null;
};

export type DashboardDetail = {
  id: string;
  name: string;
  description: string;
  status: DashboardStatus;
  /**
   * Filter keys the dashboard exposes as dashboard-level (global) filters. The
   * user picks VALUES for these at view time and the UI merges the selections
   * into every widget's `widget_filters` for `/query` — this is NOT saved values.
   */
  global_filters: string[];
  /**
   * True when the dashboard is shared to this agency. Shared dashboards are
   * read-only for everyone (internal and external) — the edit affordances are
   * disabled regardless of privilege. See `canEditByodDashboard`.
   */
  is_shared?: boolean;
  layouts: Layout; // Row[] blob — stored/returned as-is by backend
  widgets_config: Record<string, PlacementWithDef>;
  created_by: UserRef | null;
  updated_by: UserRef | null;
  time_created: string | null;
  time_updated?: string | null;
};

// ---------------------------------------------------------------------------
// Preview / Query responses
// ---------------------------------------------------------------------------

/** Direction of a KPI trend vs the comparison period. */
export type TrendDirection = 'up' | 'down' | 'neutral' | null;

export type PreviewResponseKpi = {
  widget_type: 'kpi';
  value: number | string | null;
  // Optional comparison-period trend. Present only when the backend computes a
  // trend for this KPI; surfaced in the tile behind the KPI_TREND_ENABLED flag.
  /** Value in the comparison period (e.g. previous range). Nullable. */
  comparison_value?: number | string | null;
  trend_direction?: TrendDirection;
  /** Percentage change vs the comparison period. Nullable when no data. */
  trend_percentage?: number | string | null;
};

export type PreviewResponseTable = {
  widget_type: 'table';
  columns: {key: string; label: string}[];
  rows: Record<string, unknown>[];
  table_config?: TableConfig | null;
  pagination: {total_count: number; page_size: number; offset: number};
  // Only present on query results (POST /dashboards/:id/query), not on preview
  selected_columns?: string[] | null;
  // Union of entity-attribute keys hoisted from each row's `entities_attributes`
  // by `flattenTableEntityAttributes` (at the normalize boundary). Drives the
  // entity-variable column picker. Absent/empty ⇒ feature dormant.
  all_attributes_columns?: string[];
};

export type PreviewResponseChart = {
  widget_type: 'chart';
  chart_config: ChartConfig | null;
  data: Record<string, unknown>[];
};

export type PreviewResponse =
  | PreviewResponseKpi
  | PreviewResponseTable
  | PreviewResponseChart;

export type QueryWidgetResult =
  | ({placement_id: string; status: 'ok'} & PreviewResponse)
  | {placement_id: string; status: 'error'; error: string};

// Query request — UI sends everything pre-merged per widget. `widget_filters`
// uses ByodFilterMap (=== Record<string, unknown>): values are opaque because
// special-cased filters carry a converted structured wire shape — see
// ByodFilterValue for the shapes and why it's `unknown`.
export type QueryWidgetInput = {
  placement_id: string;
  widget_key: string;
  widget_filters: ByodFilterMap;
  widget_config: Record<string, unknown>;
};

export type DashboardQueryRequest = {
  widgets: QueryWidgetInput[];
};

export type DashboardQueryResponse = {
  dashboard_id: string;
  name: string;
  widgets: QueryWidgetResult[];
};

// ---------------------------------------------------------------------------
// Publish dashboard
// ---------------------------------------------------------------------------

export type PublishDashboardResponse = {
  dashboard_id: string;
  name: string;
  status: 'published';
  /**
   * Filter keys the dashboard exposes as dashboard-level (global) filters. The
   * user picks VALUES for these at view time and the UI merges the selections
   * into every widget's `widget_filters` for `/query` — this is NOT saved values.
   */
  global_filters: string[];
};

// ---------------------------------------------------------------------------
// Update dashboard
// ---------------------------------------------------------------------------

export type WidgetConfigPayload = {
  widget_key: string;
  widget_filters: Record<string, unknown>;
  widget_config: Record<string, unknown>;
};

export type UpdateDashboardResponse = {
  id: string;
  name: string;
  /**
   * Filter keys the dashboard exposes as dashboard-level (global) filters. The
   * user picks VALUES for these at view time and the UI merges the selections
   * into every widget's `widget_filters` for `/query` — this is NOT saved values.
   */
  global_filters: string[];
  layout: Layout;
  updated_by: {id: string; name: string};
};

// ---------------------------------------------------------------------------
// Update widget
// ---------------------------------------------------------------------------

export type UpdateWidgetResponse = {
  placement_id: string;
  widget_key: string;
  widget_filters: Record<string, unknown>;
  widget_config: Record<string, unknown>;
  updated_by: {id: string; name: string};
};
